<template>
	<div
		class="d-flex flex-column justify-content-between lap-welcomestep-height"
	>
		<section
			class="onboarding-top-content invisible-scroll welcomestep-topcontent-mobile"
		>
			<section class="w-345px">
				<section class="obm-chat-container mt-36px">
					<transition name="chat-pop">
						<div v-if="animationStage >= 1" class="obm-hire3x-chat-bubble">
							<span class="first-bubble-text">
								Hi {{ displayName }}!! Looks like you are new to Hire3x
							</span>

							<img
								src="../../../assets/newonboarding/chatbubble1-img.svg"
								class="first-bubble-img"
								alt=""
							/>
						</div>
					</transition>

					<transition name="chat-pop">
						<div
							v-if="animationStage >= 2"
							class="mt-16px obm-hire3x-chat-bubble"
						>
							Tell me about yourself..
						</div>
					</transition>
				</section>

				<transition name="chat-pop">
					<section
						v-if="animationStage >= 3"
						class="obm-selection-container mt-36px d-flex flex-column align-items-center"
					>
						<div class="fs-20 fw-M text-white font-manrope">I am a...</div>
						<section class="mt-16px d-flex align-items-center gap-18px">
							<div
								v-for="type in userTypes"
								:key="type.id"
								class="obm-user-type-card"
								:class="{ active: selectedType === type.id }"
								@click="handleTypeSelect(type.id)"
							>
								<img
									v-if="selectedType === type.id"
									:src="greenTick"
									class="type-check-pos"
									width="18"
									height="18"
									alt=""
								/>
								<img :src="type.image" class="type-illustration" alt="" />

								<p
									class="m-0 font-manrope fs-13 type-label"
									:class="
										selectedType === type.id
											? 'fw-B type-label--selected'
											: 'fw-LM'
									"
								>
									{{ type.label }}
								</p>
							</div>
						</section>

						<div class="obm-or-divider">
							<span class="obm-or-line"></span>
							<span class="obm-or-text fam-inter fw-LM fs-12">Or</span>
							<span class="obm-or-line"></span>
						</div>

						<div
							class="skybluebtn fam-inter text-white fs-14 text-center d-flex flex-column align-items-center w207px"
							@click="handleUploadResume"
						>
							<span class="fw-M">Upload my resume</span>
							<span class="fw-L text-italic">(Saves 2+ steps)</span>
						</div>
					</section>
				</transition>
			</section>
		</section>
		<section
			class="onboarding-top-content onboarding-bottom-content skipfrnow-mobile"
		>
			<section class="w-345px">
				<div
					v-if="animationStage >= 3"
					class="mt-36px underlined-white-txt mt-mob-0"
					@click="handleSkip"
				>
					Skip for now
				</div>
			</section>
		</section>
	</div>
</template>

<script>
import { updateCandidateOnboard, userInfo } from "@/utils/apiFunctions";
import { trackOnboardingSkipped } from "@/utils/posthogHelpers";

export default {
	emits: ["close", "next", "open-upload-resume"],
	data() {
		return {
			resolvedFirstName: "",
			animationStage: 0,
			userTypes: [
				{
					id: "student",
					label: "Student",
					image: require("@/assets/newonboarding/student.svg"),
				},
				{
					id: "fresher",
					label: "Fresher",
					image: require("@/assets/newonboarding/fresher.svg"),
				},
				{
					id: "experienced",
					label: "Experienced",
					image: require("@/assets/newonboarding/experienced.svg"),
				},
			],
			selectedType: null,
			greenTick: require("@/assets/newonboarding/greentick.svg"),
			countdown: 0,
			_countdownInterval: null,
			_navTimer: null,
		};
	},
	props: {
		skipAnimation: {
			type: Boolean,
			default: false,
		},
		firstName: {
			type: String,
			default: "",
		},
	},
	computed: {
		displayName() {
			return this.firstName || this.resolvedFirstName || "there";
		},
	},
	async created() {
		// If firstName wasn't passed as a prop (e.g. on page refresh),
		// fetch it directly from the userInfo API.
		if (!this.firstName) {
			try {
				const res = await userInfo();
				this.resolvedFirstName =
					res?.data?.data?.firstName || res?.data?.firstName || "";
			} catch (e) {
				console.warn("[WelcomeStep] userInfo fetch failed:", e);
			}
		}

		if (this.skipAnimation) {
			this.animationStage = 4;
			return;
		}

		await this.playAnimation();
	},

	beforeDestroy() {
		this.clearTimers();
	},

	methods: {
		wait(ms) {
			return new Promise((resolve) => setTimeout(resolve, ms));
		},

		async playAnimation() {
			await this.wait(500);
			this.animationStage = 1;

			await this.wait(800);
			this.animationStage = 2;

			await this.wait(800);
			this.animationStage = 3;
		},

		async handleSkip() {
			// First-time user: mark the flag true so the first-time UI stays
			// active through the first session (flipped false on 2nd login).
			this.$store.commit("userVUEX/setIsFirstTimeUser", true);
			trackOnboardingSkipped();
			const fromTraining =
				this.$route && this.$route.query && this.$route.query.fromTraining;
			const returnUrl =
				this.$route && this.$route.query && this.$route.query.returnUrl;

			const payload = { action: "skip" };

			if (fromTraining && returnUrl) {
				// Call the skip API first, close the onboarding modal, then reroute
				// back to the external training project so the redirect only happens
				// after the onboarding state has been fully saved and the modal closed.
				try {
					const response = await updateCandidateOnboard(payload);

					await this.$store.dispatch("userVUEX/fetchPostLoginData", true);
				} catch (err) {
					console.error("Skip onboarding error:", err);
				}
				this.$store.commit("candidateVUEX/setOnboardingActive", false);
				this.$emit("close");
				setTimeout(() => {
					window.location.href = decodeURIComponent(returnUrl);
				}, 150);
			} else {
				// Close modal first, then fire API in background
				this.$store.commit("candidateVUEX/setOnboardingActive", false);
				this.$emit("close");

				try {
					const response = await updateCandidateOnboard(payload);

					await this.$store.dispatch("userVUEX/fetchPostLoginData", true);
				} catch (err) {
					console.error("Skip onboarding error:", err);
				}
			}
		},

		async handleUploadResume() {
			trackOnboardingSkipped();
			try {
				const payload = { action: "resumeUpload" };
				console.log("resumeUpload payload", payload);
				const response = await updateCandidateOnboard(payload);
				console.log("updateCandidateOnboard response:", response);
			} catch (err) {
				console.error("Resume upload onboarding error:", err);
			}
			sessionStorage.setItem("fromOnboarding", "true");
			this.$emit("open-upload-resume");
		},

		clearTimers() {
			if (this._navTimer) {
				clearTimeout(this._navTimer);
				this._navTimer = null;
			}
			if (this._countdownInterval) {
				clearInterval(this._countdownInterval);
				this._countdownInterval = null;
			}
		},

		handleTypeSelect(typeId) {
			// If same card clicked again, cancel pending navigation
			if (this.selectedType === typeId) {
				this.selectedType = null;
				this.countdown = 0;
				this.clearTimers();
				return;
			}

			// Cancel any running timer from a previous selection
			this.clearTimers();

			this.selectedType = typeId;
			this.countdown = 1;

			// Tick countdown every second
			this._countdownInterval = setInterval(() => {
				this.countdown -= 1;
				if (this.countdown <= 0) {
					clearInterval(this._countdownInterval);
					this._countdownInterval = null;
				}
			}, 1000);

			// Navigate after 4 seconds
			this._navTimer = setTimeout(() => {
				this.$emit("next", this.selectedType);
			}, 1000);
		},
	},
};
</script>

<style scoped></style>
